import React, { useState, useMemo } from 'react';
import { useEventPageContext } from '../../context/EventPageContext';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { MdIosShare, MdSettings } from 'react-icons/md';
import { IoMdBarcode } from 'react-icons/io';
import TextAllGuestsModal from 'legacy/events/components/guest-list/TextAllGuestsModal';
import { RiMessage2Line } from 'react-icons/ri';
import { useGetCanTextAll } from 'legacy/user/hooks';
import { useTranslation } from 'react-i18next';
import { EventForHost } from 'legacy/events/types';

interface ActionConfig {
  key: string;
  testId?: string;
  icon: React.ReactNode;
  labelKey: string;
  onClick: () => void;
  condition?: boolean;
  mobileRounded?: boolean;
  labelClassName?: string;
}

const RoleBasedActionBar = () => {
  const { t } = useTranslation(['events']);
  const {
    event,
    toggleScanQRModal,
    toggleShareModal,
    toggleManageEvent,
    canShare
  } = useEventPageContext();

  const { data: textAllSettings } = useGetCanTextAll();
  const [showTextAllGuests, setShowTextAllGuests] = useState<boolean>(false);

  const toggleTextAllGuests = () => {
    setShowTextAllGuests((prevState) => !prevState);
  };

  const roleActions = useMemo(() => {
    const actions: Record<string, ActionConfig[]> = {
      organizer: [
        {
          key: 'share',
          testId: 'button-share-event',
          icon: <MdIosShare size={20} />,
          labelKey: 'events:actions.shareEvent',
          onClick: toggleShareModal,
          condition: canShare,
          mobileRounded: true
        },
        {
          key: 'scan',
          testId: 'button-scan-ticket',
          icon: <IoMdBarcode size={20} />,
          labelKey: 'events:actions.scanTickets',
          onClick: toggleScanQRModal,
          mobileRounded: true
        },
        {
          key: 'textGuests',
          icon: <RiMessage2Line size={20} />,
          labelKey: 'events:actions.textGuests',
          onClick: toggleTextAllGuests,
          condition: textAllSettings?.canTextAll,
          mobileRounded: true
        },
        {
          key: 'manage',
          testId: 'button-manage-event',
          icon: <MdSettings size={20} />,
          labelKey: 'events:actions.manageEvent',
          onClick: toggleManageEvent,
          labelClassName: 'block'
        }
      ],
      scanner: [
        {
          key: 'share',
          testId: 'button-share-event',
          icon: <MdIosShare size={20} />,
          labelKey: 'events:actions.shareEvent',
          onClick: toggleShareModal,
          condition: canShare,
          labelClassName: 'block'
        },
        {
          key: 'scan',
          testId: 'button-scan-ticket',
          icon: <IoMdBarcode size={20} />,
          labelKey: 'events:actions.scanTickets',
          onClick: toggleScanQRModal,
          labelClassName: 'block'
        }
      ],
      photographer: [
        {
          key: 'share',
          testId: 'button-share-event',
          icon: <MdIosShare size={20} />,
          labelKey: 'events:actions.shareEvent',
          onClick: toggleShareModal,
          condition: canShare,
          labelClassName: 'block'
        },
        {
          key: 'manage',
          testId: 'button-manage-event',
          icon: <MdSettings size={20} />,
          labelKey: 'events:actions.manageEvent',
          onClick: toggleManageEvent,
          labelClassName: 'block'
        }
      ]
    };

    return actions;
  }, [
    canShare,
    toggleShareModal,
    toggleScanQRModal,
    toggleTextAllGuests,
    toggleManageEvent,
    textAllSettings?.canTextAll
  ]);

  const renderAction = (action: ActionConfig) => {
    if (action.condition === false) {
return null;
}

    return (
      <Button
        key={action.key}
        testId={action.testId}
        type={ButtonType.Inverted}
        icon={action.icon}
        onClick={action.onClick}
        mobileRounded={action.mobileRounded}
      >
        <span className={action.labelClassName || 'hidden md:block'}>
          {t(action.labelKey)}
        </span>
      </Button>
    );
  };

  const getCurrentRoleActions = (): ActionConfig[] => {
    if (event.isOrganizer) {
return roleActions.organizer;
}
    if (event.isScanner) {
return roleActions.scanner;
}
    if (event.isPhotographer) {
return roleActions.photographer;
}
    return [];
  };

  return (
    <div className="bg-white pb-8 md:pb-4">
      <div className="px-5 pt-4">
        <div className="md:max-w-[800px] md:mx-auto flex items-center justify-between gap-2">
          {getCurrentRoleActions().map(renderAction)}

          <TextAllGuestsModal
            eventId={event._id}
            tiers={(event as EventForHost).pricing?.tiers ?? []}
            totalGuests={event.totalGuests ?? 0}
            visible={showTextAllGuests}
            onClose={toggleTextAllGuests}
          />
        </div>
      </div>
    </div>
  );
};

export default RoleBasedActionBar;
